import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/system";
import Backdrop from "@mui/material/Backdrop";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AlertMessage from "../../common/components/AlertMessage";
import Button from "../../common/components/Button";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL";
import { getSessionContext } from "../../common/utils/sessionContext";
import "../../styles/AddVitals.css";

const CustomTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#FCB040",
    fontWeight: "bold",
  },

  "& .MuiInputLabel-root": {
    fontSize: "16px",
  },

  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#FCB040",
    },
    "&:hover fieldset": {
      borderColor: "#B2BAC2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#FCB040",
    },
  },

  "@media (max-height: 700px)": {
    "& .MuiInputLabel-root": {
      fontSize: "14px",
    },
  },
});

const AddVitals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showButtonState, setShowButtonState] = useState(true);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [vitalData, setVitalData] = useState({
    date: "",
    time: "",
    leftArmBP: "",
    rightArmBP: "",
    currentHeartRate: "",
    restingHeartRate: "",
    bodyTemp: "",
    bloodSugar: "",
    oxygenLevel: "",
    weight: "",
  });

  const closeAlert = () => {
    setAlertStatusBase("");
  };

  const updateVitalDetails = (e) => {
    const key = e.target.name;
    let value = e.target.value;
    value = value.replace(/^[\s!@#$%^&*()]+/, "");
    const updated = { ...vitalData, [key]: value };
    setVitalData(updated);

    if ((key === "date" && value !== "" && updated.time !== "") || (key === "time" && updated.date !== "" && value !== "")) {
      setShowButtonState(false);
    } else if (updated.date === "" || updated.time === "") {
      setShowButtonState(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const { caregiverId, residentId, residentName } = getSessionContext();

      const data = {
        caregiverId,
        clientId: residentId,
        clientName: residentName,
        vitalInformation: JSON.stringify(vitalData),
      };

      const responseData = await APIService.postData(URL.addVitalInfoByCaregiver, data);

      const isSuccess = String(responseData?.code) === "200";

      if (!isSuccess) {
        setAlertStatusBase({
          message: responseData?.message || "Failed to save vitals",
          status: String(responseData?.code ?? "400"),
          origin: "center",
        });
        return;
      }
      setVitalData({
        date: "",
        time: "",
        leftArmBP: "",
        rightArmBP: "",
        currentHeartRate: "",
        restingHeartRate: "",
        bodyTemp: "",
        bloodSugar: "",
        oxygenLevel: "",
        weight: "",
      });

      setShowButtonState(true);
      setSuccessMessage(responseData.message || "Vitals saved successfully");
      setShowSuccessDialog(true);
    } catch (error) {
      setAlertStatusBase({
        message: "Error while adding vitals",
        status: "400",
        origin: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessOk = () => {
    setShowSuccessDialog(false);

    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/vitals");
    }
  };

  return (
    <>
      <Header />
      <div className="home-container add-vitals-page">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" />
        </Backdrop>
        <Dialog
          className="add-vitals-success-dialog"
          open={showSuccessDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            handleSuccessOk();
          }}
          aria-labelledby="add-vitals-success-title"
          maxWidth="xs"
          fullWidth
          PaperProps={{ className: "add-vitals-success-paper" }}
          BackdropProps={{
            sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
          }}>
          <DialogContent className="add-vitals-success-content">
            <p id="add-vitals-success-title" className="add-vitals-success-message">
              {successMessage}
            </p>
          </DialogContent>
          <DialogActions className="add-vitals-success-actions">
            <Button name="ok" label="OK" className="confirm-success-button" onClick={handleSuccessOk} />
          </DialogActions>
        </Dialog>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}
        <div className="home-inner-container add-vitals-inner" style={{ justifyContent: "flex-start" }}>
          <div className="heading vitals-heading">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={() => navigate("/vitals")} />
            <span className="av-page-title">ADD VITAL INFORMATION</span>
            <span></span>
          </div>

          <div className="task-container add-vitals-form-container col-11 col-sm-11 col-md-8 col-lg-4 col-xl-4" style={{ cursor: "default", padding: "0px" }}>
            <div className="add-vitals-form-body">
              <div className="av-row">
                <div className="av-label">
                  Report Date & Time <span className="required">*</span>
                </div>
                <div className="av-fields">
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused type="date" label="MM/DD/YYYY" variant="outlined" name="date" value={vitalData.date} onChange={updateVitalDetails} />
                  </div>
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused type="time" label="HH:MM" variant="outlined" name="time" value={vitalData.time} onChange={updateVitalDetails} />
                  </div>
                </div>
              </div>

              <div className="av-row">
                <div className="av-label">Blood Pressure</div>
                <div className="av-fields">
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused label="Left Arm" variant="outlined" helperText="High / Low" name="leftArmBP" value={vitalData.leftArmBP} onChange={updateVitalDetails} />
                  </div>
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused label="Right Arm" variant="outlined" helperText="High / Low" name="rightArmBP" value={vitalData.rightArmBP} onChange={updateVitalDetails} />
                  </div>
                </div>
              </div>

              <div className="av-row">
                <div className="av-label">Heart Rate</div>
                <div className="av-fields">
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused label="Current rate" variant="outlined" helperText="BPM" name="currentHeartRate" value={vitalData.currentHeartRate} onChange={updateVitalDetails} inputProps={{ inputMode: "decimal", pattern: "[0-9]*" }} />
                  </div>
                  <div className="av-field">
                    <CustomTextField style={{ width: "100%" }} focused label="Resting rate" variant="outlined" helperText="BPM" name="restingHeartRate" value={vitalData.restingHeartRate} onChange={updateVitalDetails} inputProps={{ inputMode: "decimal", pattern: "[0-9]*" }} />
                  </div>
                </div>
              </div>

              <div className="av-row">
                <div className="av-fields">
                  <div className="av-field">
                    <div className="av-label">Body Temperature</div>
                    <CustomTextField style={{ width: "100%" }} focused variant="outlined" helperText="Fahrenheit" name="bodyTemp" value={vitalData.bodyTemp} onChange={updateVitalDetails} inputProps={{ inputMode: "decimal", pattern: "[0-9]*" }} />
                  </div>
                  <div className="av-field">
                    <div className="av-label">Blood Glucose</div>
                    <CustomTextField style={{ width: "100%" }} focused variant="outlined" helperText="mmol/L" name="bloodSugar" value={vitalData.bloodSugar} onChange={updateVitalDetails} inputProps={{ inputMode: "numeric" }} />
                  </div>
                </div>
              </div>

              <div className="av-row av-row-last">
                <div className="av-fields">
                  <div className="av-field">
                    <div className="av-label">Oxygen Level</div>
                    <CustomTextField style={{ width: "100%" }} focused variant="outlined" helperText="%" name="oxygenLevel" value={vitalData.oxygenLevel} onChange={updateVitalDetails} inputProps={{ inputMode: "decimal", pattern: "[0-9]*" }} />
                  </div>
                  <div className="av-field">
                    <div className="av-label">Weight</div>
                    <CustomTextField style={{ width: "100%" }} focused variant="outlined" helperText="lbs" name="weight" value={vitalData.weight} onChange={updateVitalDetails} inputProps={{ inputMode: "decimal", pattern: "[0-9]*" }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="btn-div">
              <Button name="cancel" label="Cancel" className="caregiver-cancel-button" onClick={() => navigate("/vitals")} />
              <Button name="submit" label="Save" className={showButtonState ? "disabled confirm-success-button" : "confirm-success-button"} onClick={handleSubmit} disabled={showButtonState} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddVitals;
